/**
 * Call-Script Settings — editable 5-step framework.
 * Route: /v3/settings/call-script
 * Backend: GET/PUT/POST-reset /api/v3/call-mode/script
 */
import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, Plus, Trash2, RotateCcw, Save, GripVertical, ArrowLeft, Info,
} from "lucide-react";
import { Link } from "react-router-dom";

const EMPTY_STEP = () => ({ key: "", label: "", prompt: "", helper: "" });

export default function CallScriptSettingsPage() {
  const [steps, setSteps]     = useState(null);
  const [isDefault, setDef]   = useState(true);
  const [saving, setSaving]   = useState(false);
  const [resetting, setReset] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/v3/call-mode/script");
      setSteps(data.steps || []);
      setDef(!!data.is_default);
    } catch {
      toast.error("Failed to load script");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const patchStep = (i, patch) => {
    setSteps((s) => s.map((st, idx) => idx === i ? { ...st, ...patch } : st));
  };
  const move = (i, dir) => {
    setSteps((s) => {
      const next = [...s];
      const j = i + dir;
      if (j < 0 || j >= next.length) return s;
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };
  const remove = (i) => setSteps((s) => s.filter((_, idx) => idx !== i));
  const add    = ()  => setSteps((s) => [...s, EMPTY_STEP()]);

  const save = async () => {
    // Simple sanity — no empty keys/labels.
    for (const st of steps || []) {
      if (!st.key.trim() || !st.label.trim()) {
        return toast.error("Every step needs a key and a label");
      }
    }
    setSaving(true);
    try {
      await api.put("/v3/call-mode/script", { steps });
      toast.success("Script saved");
      setDef(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const restoreDefaults = async () => {
    if (!window.confirm("Restore the shipped default script? Your edits will be lost.")) return;
    setReset(true);
    try {
      const { data } = await api.post("/v3/call-mode/script/reset");
      setSteps(data.steps || []);
      setDef(true);
      toast.success("Defaults restored");
    } catch { toast.error("Reset failed"); }
    finally { setReset(false); }
  };

  return (
    <div className="p-6 lg:p-8 max-w-4xl mx-auto" data-testid="call-script-settings">
      <Link to="/v3" className="text-[12px] text-slate-500 hover:text-indigo-600 inline-flex items-center gap-1 mb-2">
        <ArrowLeft className="w-3 h-3" /> Back to dashboard
      </Link>
      <div className="mb-6">
        <div className="text-[11px] font-black uppercase tracking-[0.18em] text-indigo-600 mb-1">
          Call Mode · Settings
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight text-slate-900">
          Your Guided Seller Call
        </h1>
        <p className="text-slate-500 text-sm mt-1.5 max-w-2xl">
          Rewrite any step below. Use <code>{"{seller_first_name}"}</code>,{" "}
          <code>{"{property_address}"}</code>, <code>{"{operator_name}"}</code>,
          or <code>{"{their_price}"}</code> anywhere in a prompt — they fill in
          live during the call.
        </p>
      </div>

      {steps == null ? (
        <div className="h-48 grid place-items-center"><Loader2 className="w-5 h-5 text-slate-400 animate-spin" /></div>
      ) : (
        <>
          {isDefault && (
            <div className="mb-4 rounded-lg bg-indigo-50 border border-indigo-200 px-3 py-2 text-[12.5px] text-indigo-900 flex items-start gap-2" data-testid="css-default-banner">
              <Info className="w-3.5 h-3.5 mt-0.5" />
              You're viewing the shipped defaults. Edit below and hit Save to make this your personal script.
            </div>
          )}

          <div className="space-y-3">
            {steps.map((st, i) => (
              <div
                key={i}
                className="rounded-2xl border border-slate-200 bg-white p-4"
                data-testid={`css-step-${i}`}
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-[10px] font-black text-slate-400 tabular-nums w-4">
                    {i + 1}
                  </span>
                  <input
                    value={st.label}
                    onChange={(e) => patchStep(i, { label: e.target.value })}
                    placeholder="Step title (e.g. Confirm they want to sell)"
                    className="flex-1 text-[15px] font-black text-slate-900 border-0 focus:outline-none focus:ring-0 bg-transparent"
                    data-testid={`css-step-${i}-label`}
                  />
                  <div className="flex items-center gap-1 shrink-0">
                    <button type="button" onClick={() => move(i, -1)} disabled={i === 0}
                            className="text-slate-400 hover:text-slate-700 disabled:opacity-30 text-[11px] font-bold px-1.5 py-1">↑</button>
                    <button type="button" onClick={() => move(i, +1)} disabled={i === steps.length - 1}
                            className="text-slate-400 hover:text-slate-700 disabled:opacity-30 text-[11px] font-bold px-1.5 py-1">↓</button>
                    <button type="button" onClick={() => remove(i)}
                            className="text-slate-400 hover:text-rose-600 p-1"
                            data-testid={`css-step-${i}-delete`}
                            title="Delete step">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                  <div>
                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1 block">Key</label>
                    <input
                      value={st.key}
                      onChange={(e) => patchStep(i, { key: e.target.value.replace(/[^a-z0-9_]/gi, "_").toLowerCase() })}
                      placeholder="confirm"
                      className="w-full text-[13px] border border-slate-200 rounded-md px-2 py-1.5 focus:outline-none focus:border-indigo-400"
                      data-testid={`css-step-${i}-key`}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1 block">Helper (shown to you, not read out loud)</label>
                    <input
                      value={st.helper}
                      onChange={(e) => patchStep(i, { helper: e.target.value })}
                      placeholder="Direct opener. If they say no, thank them and end the call."
                      className="w-full text-[13px] border border-slate-200 rounded-md px-2 py-1.5 focus:outline-none focus:border-indigo-400"
                      data-testid={`css-step-${i}-helper`}
                    />
                  </div>
                </div>
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1 block">Prompt (what you say)</label>
                <textarea
                  value={st.prompt}
                  onChange={(e) => patchStep(i, { prompt: e.target.value })}
                  rows={2}
                  placeholder="Hey {seller_first_name}, are you still looking to sell {property_address}?"
                  className="w-full text-[13px] border border-slate-200 rounded-md p-2 focus:outline-none focus:border-indigo-400 resize-none"
                  data-testid={`css-step-${i}-prompt`}
                />
              </div>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-2 mt-4">
            <button type="button" onClick={add}
                    className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white text-slate-700 hover:border-indigo-400 text-[13px] font-bold px-3 py-2"
                    data-testid="css-add-step">
              <Plus className="w-3.5 h-3.5" /> Add a step
            </button>
            <button type="button" onClick={restoreDefaults} disabled={resetting}
                    className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white text-slate-500 hover:border-slate-300 text-[13px] font-bold px-3 py-2 disabled:opacity-50"
                    data-testid="css-restore-defaults">
              {resetting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />} Restore defaults
            </button>
            <button type="button" onClick={save} disabled={saving}
                    className="ml-auto inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-sm px-4 py-2.5 disabled:opacity-50"
                    data-testid="css-save">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save script
            </button>
          </div>
        </>
      )}
    </div>
  );
}
